/**
 * Project Name: Brainsie eCommerce v4
 * Component: Infographic Section Alternative
 * Description: Text content on left, image on right - two column layout
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import ComponentRenderer from "../ComponentRenderer";

export default function InfographicSectionAlternative({
  section,
  components,
  loading,
  error,
  pageData,
  darkMode,
  theme,
  sectionSettings: passedSettings,
  getSectionStyles: passedGetSectionStyles,
  getHeadingColor: passedGetHeadingColor,
  getSubHeadingColor: passedGetSubHeadingColor,
  getBodyColor: passedGetBodyColor,
}) {
  console.log(
    "🟠 InfographicSectionAlternative RENDERING - TEXT LEFT, IMAGE RIGHT"
  );
  // Get section settings with comprehensive defaults
  const sectionSettings =
    passedSettings ||
    (section.Section_Settings
      ? JSON.parse(section.Section_Settings)
      : {
          paddingY: 8,
          paddingX: 0,
          maxWidth: "lg",
          backgroundColor: "",
          textColor: "",
          headingColor: "",
          subHeadingColor: "",
          bodyColor: "",
          overlayColor: "rgba(0, 0, 0, 0.5)",
          enableBackgroundImage: false,
          textAlign: "left",
          customStyles: "",
        });

  // Enhanced styling functions
  const getSectionStyles =
    passedGetSectionStyles ||
    (() => {
      const baseStyles = {
        py: sectionSettings.paddingY || 8,
        px: sectionSettings.paddingX || 0,
        position: "relative",
        textAlign: sectionSettings.textAlign || "left",
        color:
          sectionSettings.textColor ||
          (darkMode ? theme.palette.common.white : theme.palette.text.primary),
      };

      if (sectionSettings.backgroundColor) {
        baseStyles.backgroundColor = sectionSettings.backgroundColor;
      } else {
        baseStyles.backgroundColor = darkMode
          ? theme.palette.grey[800]
          : theme.palette.background.paper;
      }

      // Handle section background image with BASEURL
      if (sectionSettings.enableBackgroundImage && section.Section_Media) {
        const imageUrl = section.Section_Media.startsWith("http")
          ? section.Section_Media
          : `${process.env.BASEURL || ""}${section.Section_Media}`;

        baseStyles.backgroundImage = `url(${imageUrl})`;
        baseStyles.backgroundSize = "cover";
        baseStyles.backgroundPosition = "center";
        baseStyles.backgroundRepeat = "no-repeat";

        if (sectionSettings.overlayColor) {
          baseStyles["&::before"] = {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: sectionSettings.overlayColor,
            zIndex: 1,
          };
          baseStyles["& > *"] = {
            position: "relative",
            zIndex: 2,
          };
        }
      }

      if (sectionSettings.customStyles) {
        try {
          const customStyles =
            typeof sectionSettings.customStyles === "string"
              ? JSON.parse(sectionSettings.customStyles)
              : sectionSettings.customStyles;
          Object.assign(baseStyles, customStyles);
        } catch (error) {
          console.warn("Invalid custom styles:", sectionSettings.customStyles);
        }
      }

      return baseStyles;
    });

  // Color helper functions with fallbacks
  const getHeadingColor =
    passedGetHeadingColor ||
    (() =>
      sectionSettings.headingColor ||
      sectionSettings.textColor ||
      (darkMode ? theme.palette.common.white : theme.palette.text.primary));

  const getSubHeadingColor =
    passedGetSubHeadingColor ||
    (() =>
      sectionSettings.subHeadingColor ||
      sectionSettings.textColor ||
      (darkMode ? theme.palette.grey[300] : theme.palette.text.secondary));

  const getBodyColor =
    passedGetBodyColor ||
    (() =>
      sectionSettings.bodyColor ||
      sectionSettings.textColor ||
      (darkMode ? theme.palette.grey[400] : theme.palette.text.primary));

  const getTextColor = () =>
    sectionSettings.textColor ||
    (darkMode ? theme.palette.common.white : theme.palette.text.primary);

  return (
    <Box
      component="section"
      className="infographic-section-alternative"
      sx={getSectionStyles()}
    >
      <Container
        maxWidth={sectionSettings.maxWidth || "lg"}
        sx={{ textAlign: sectionSettings.textAlign || "left" }}
      >
        <Grid
          container
          spacing={4}
          alignItems={(() => {
            // Check if we have a Full Vertical Image component
            const hasFullVerticalImage = components?.some(
              (component) => component.Component_Name === "Full Vertical Image"
            );
            return hasFullVerticalImage ? "center" : "flex-start";
          })()}
          sx={{
            display: "flex",
            flexWrap: "wrap",
          }}
        >
          {/* First Column - Text Content (displays on LEFT) */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              pr: { md: 3 },
            }}
          >
            {/* Section Header with enhanced styling */}
            {section.Section_Heading && section.Section_Heading !== "NA" && (
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  mt: 2,
                  mb: 3,
                  color: getHeadingColor(),
                  fontWeight: 600,
                  fontSize: { xs: "1.75rem", md: "2.125rem" },
                  lineHeight: 1.2,
                  textAlign: sectionSettings.textAlign || "left",
                }}
              >
                {section.Section_Heading}
              </Typography>
            )}

            {/* Section Sub Heading */}
            {section.Section_Sub_Heading &&
              section.Section_Sub_Heading !== "NA" && (
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{
                    mb: 2,
                    color: getSubHeadingColor(),
                    fontWeight: 500,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    lineHeight: 1.3,
                    textAlign: sectionSettings.textAlign || "left",
                  }}
                >
                  {section.Section_Sub_Heading}
                </Typography>
              )}

            {/* Section Sub Text */}
            {section.Section_Sub_Text && section.Section_Sub_Text !== "NA" && (
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: getBodyColor(),
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  textAlign: sectionSettings.textAlign || "left",
                }}
              >
                {section.Section_Sub_Text}
              </Typography>
            )}

            {/* Render text components in specific order */}
            {loading ? (
              <Box sx={{ color: getTextColor() }}>Loading components...</Box>
            ) : error ? (
              <Box sx={{ color: theme.palette.error.main }}>
                Error loading components: {error.message}
              </Box>
            ) : (
              <Box sx={{ position: "relative", minHeight: "200px" }}>
                {/* Main content area */}
                <Box>
                  {/* First render paragraphs */}
                  {components
                    ?.filter(
                      (component) =>
                        component.Component_Name === "Default Paragraph"
                    )
                    ?.map((component) => (
                      <ComponentRenderer
                        key={component.Component_Id}
                        component={component}
                        section={section}
                        darkMode={darkMode}
                        theme={theme}
                        componentSettings={component.Settings}
                        sectionSettings={sectionSettings}
                        getSectionStyles={getSectionStyles}
                        getHeadingColor={getHeadingColor}
                        getSubHeadingColor={getSubHeadingColor}
                        getBodyColor={getBodyColor}
                      />
                    ))}

                  {/* Then render lists */}
                  {components
                    ?.filter(
                      (component) => component.Component_Name === "Default List"
                    )
                    ?.map((component) => (
                      <ComponentRenderer
                        key={component.Component_Id}
                        component={component}
                        section={section}
                        darkMode={darkMode}
                        theme={theme}
                        componentSettings={component.Settings}
                        sectionSettings={sectionSettings}
                        getSectionStyles={getSectionStyles}
                        getHeadingColor={getHeadingColor}
                        getSubHeadingColor={getSubHeadingColor}
                        getBodyColor={getBodyColor}
                      />
                    ))}
                </Box>

                {/* CTA positioned at bottom right */}
                {components
                  ?.filter(
                    (component) => component.Component_Name === "Learn More CTA"
                  )
                  ?.map((component) => (
                    <ComponentRenderer
                      key={component.Component_Id}
                      component={component}
                      section={section}
                      darkMode={darkMode}
                      theme={theme}
                      componentSettings={component.Settings}
                      sectionSettings={sectionSettings}
                      getSectionStyles={getSectionStyles}
                      getHeadingColor={getHeadingColor}
                      getSubHeadingColor={getSubHeadingColor}
                      getBodyColor={getBodyColor}
                    />
                  ))}

                {/* Render any other components not specifically handled */}
                {components
                  ?.filter(
                    (component) =>
                      component.Component_Name !== "Default Square Image" &&
                      component.Component_Name !== "Full Vertical Image" &&
                      component.Component_Name !== "Default Paragraph" &&
                      component.Component_Name !== "Default List" &&
                      component.Component_Name !== "Learn More CTA"
                  )
                  ?.map((component) => (
                    <ComponentRenderer
                      key={component.Component_Id}
                      component={component}
                      section={section}
                      darkMode={darkMode}
                      theme={theme}
                      componentSettings={component.Settings}
                      sectionSettings={sectionSettings}
                      getSectionStyles={getSectionStyles}
                      getHeadingColor={getHeadingColor}
                      getSubHeadingColor={getSubHeadingColor}
                      getBodyColor={getBodyColor}
                    />
                  ))}
              </Box>
            )}
          </Grid>

          {/* Second Column - Image Components (displays on RIGHT) */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "400px",
            }}
          >
            {loading ? (
              <Box sx={{ color: getTextColor() }}>Loading components...</Box>
            ) : error ? (
              <Box sx={{ color: theme.palette.error.main }}>
                Error loading components: {error.message}
              </Box>
            ) : (
              <>
                {/* Render only the first image component (prioritize Full Vertical Image) */}
                {(() => {
                  const imageComponents =
                    components?.filter(
                      (component) =>
                        component.Component_Name === "Default Square Image" ||
                        component.Component_Name === "Full Vertical Image"
                    ) || [];

                  // Prioritize Full Vertical Image over Default Square Image
                  const verticalImage = imageComponents.find(
                    (c) => c.Component_Name === "Full Vertical Image"
                  );
                  const squareImage = imageComponents.find(
                    (c) => c.Component_Name === "Default Square Image"
                  );
                  const selectedImage = verticalImage || squareImage;

                  return selectedImage ? (
                    <ComponentRenderer
                      key={selectedImage.Component_Id}
                      component={selectedImage}
                      section={section}
                      darkMode={darkMode}
                      theme={theme}
                      componentSettings={selectedImage.Settings}
                      sectionSettings={sectionSettings}
                      getSectionStyles={getSectionStyles}
                      getHeadingColor={getHeadingColor}
                      getSubHeadingColor={getSubHeadingColor}
                      getBodyColor={getBodyColor}
                      getTextColor={getTextColor}
                    />
                  ) : null;
                })()}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
